import { Text } from '@lifeomic/chroma-react/components/Text';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { Sparkle } from '@lifeomic/chromicons';
import React, { useContext } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import SurveyItem from '../util/SurveyItem';
import {
  RepeatingGroupInstanceContext,
  SurveyContext,
  SurveyResponseContext,
} from './Contexts';

const messages = defineMessages({
  indicator: {
    id: 'surveys.filledBy.indicator',
    defaultMessage: 'Pre-filled',
  },
  fhirFirst: {
    id: 'surveys.filledBy.fhirFirst',
    defaultMessage: 'Pre-filled from FHIR/chart',
  },
  zori: {
    id: 'surveys.filledBy.zori',
    defaultMessage: 'Pre-filled by Zori',
  },
  other: {
    id: 'surveys.filledBy.other',
    defaultMessage: 'Pre-filled ({source})',
  },
});

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'inline-flex',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
    cursor: 'help',
  },
  icon: {
    width: theme.pxToRem(14),
    height: theme.pxToRem(14),
  },
  // Chroma Tooltip types `title` as string and collapses bare "\n" unless
  // white-space is pre-line on the tooltip root.
  tooltip: {
    whiteSpace: 'pre-line',
  },
}));

export const testIds = {
  indicator: (linkId: string) => `survey-filledBy-${linkId}`,
};

export type FilledByIndicatorProps = {
  surveyItem: SurveyItem;
};

const FilledByIndicator: React.FC<FilledByIndicatorProps> = ({
  surveyItem,
}) => {
  const classes = useStyles({});
  const { formatMessage } = useIntl();
  const survey = useContext(SurveyContext);
  const response = useContext(SurveyResponseContext);
  const repeatingInstance = useContext(RepeatingGroupInstanceContext);

  if (!survey || !response || surveyItem.hasCalculatedValue) return null;

  const fieldName = `${repeatingInstance?.namePrefix || ''}${surveyItem.link}`;
  const responseItem = response.getItemAtPath(
    survey.fieldNameToUpdatePath(fieldName),
  );
  const provenance = responseItem?.fillProvenance;
  if (!provenance) return null;

  const sourceText =
    provenance.source === 'fhir-first'
      ? formatMessage(messages.fhirFirst)
      : provenance.source === 'zori'
      ? formatMessage(messages.zori)
      : formatMessage(messages.other, { source: provenance.source });
  // Keep title typed as string; pre-line className preserves newline breaks.
  const tooltip = [sourceText, ...provenance.citations].join('\n');

  return (
    <Tooltip title={tooltip} className={classes.tooltip}>
      {/*
        Chroma/Reakit TooltipReference clones this child and adds ref +
        tabIndex={0} + mouse/focus handlers. A native span is valid; no
        extra focus wrapper is required for hover/focus to open the tip.
      */}
      <span
        className={classes.root}
        aria-label={tooltip}
        data-testid={testIds.indicator(surveyItem.link)}
      >
        <Sparkle aria-hidden className={classes.icon} />
        <Text size="caption">{formatMessage(messages.indicator)}</Text>
      </span>
    </Tooltip>
  );
};

export default FilledByIndicator;
